import {
  departureTime,
  travelOf,
  formatTimeSl,
  type CheckinStatus,
  type Occurrence,
  type Progress,
} from '@planer/shared';
import { Check, PartyPopper, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { occColor } from '../lib/colors';
import { timeLabel, untilLabel } from '../lib/format';

interface NowCardProps {
  now: Date;
  /** Running now, or the next one to start. */
  focus: { occ: Occurrence; running: boolean } | null;
  status: CheckinStatus | null | undefined;
  progress: Progress | undefined;
  onStatus: (occ: Occurrence, status: CheckinStatus) => void;
  onOpen: (occ: Occurrence) => void;
}

/** The answer to "what now?": current or next activity, with one-tap check-in. */
export function NowCard({ now, focus, status, progress, onStatus, onOpen }: NowCardProps) {
  if (!focus) {
    if (!progress || progress.total === 0) return null;
    const allDone = progress.open === 0;
    return (
      <section
        className="card flex items-center gap-4 p-4"
        aria-label={allDone ? 'Dan je opravljen' : 'Preostanek dneva'}
        data-testid="now-card"
      >
        <span
          className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${
            allDone ? 'bg-done-soft text-done-ink' : 'bg-mid-soft text-mid-ink'
          }`}
          aria-hidden
        >
          {allDone ? <PartyPopper size={24} /> : <Check size={24} />}
        </span>
        <div>
          <p className="text-[17px] font-semibold">
            {allDone ? 'Za danes je vse označeno' : `Še ${progress.open} za označiti`}
          </p>
          <p className="text-[14px] text-muted">
            {allDone
              ? `${progress.done} od ${progress.total} opravljeno. Lepo!`
              : 'Odkljukaj, kar si naredil, in označi, kar ni šlo.'}
          </p>
        </div>
      </section>
    );
  }

  const { occ, running } = focus;
  const start = new Date(occ.start);
  const end = new Date(occ.end);
  const elapsed = running
    ? Math.min(1, (now.getTime() - start.getTime()) / Math.max(1, end.getTime() - start.getTime()))
    : 0;
  const color = occColor(occ);
  // With travel, what matters before the start is when to leave.
  const travelMin = travelOf(occ).before;
  const leaveAt = new Date(start.getTime() - travelMin * 60_000);
  const leave = departureTime(occ);
  const countdown = running
    ? occ.durationMin > 0
      ? `še ${untilLabel(now, end)}`
      : ''
    : leave && now < leaveAt
      ? `odhod čez ${untilLabel(now, leaveAt)}`
      : leave
        ? 'na poti'
        : `čez ${untilLabel(now, start)}`;

  return (
    <section
      className="card relative overflow-hidden"
      aria-label={running ? 'Zdaj' : 'Naslednje'}
      data-testid="now-card"
    >
      <span className="absolute inset-y-0 left-0 w-1.5" style={{ background: color }} aria-hidden />
      <button
        type="button"
        className="block w-full px-5 pt-4 pb-3 text-left"
        onClick={() => {
          onOpen(occ);
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <span className="section-label" style={{ color }}>
            {running ? 'Zdaj' : 'Naslednje'}
          </span>
          <span className="text-[13px] font-medium text-muted tabular-nums">{countdown}</span>
        </div>
        <h2 className="mt-1.5 text-[24px] leading-tight font-bold tracking-tight">{occ.title}</h2>
        <p className="mt-0.5 text-[15px] text-muted tabular-nums">
          {timeLabel(occ)}
          {leave && !running ? ` · odhod ob ${formatTimeSl(leave)}` : ''}
        </p>
        {running && occ.durationMin > 0 && (
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-2" aria-hidden>
            <motion.div
              className="h-full rounded-full"
              style={{ background: color }}
              initial={false}
              animate={{ width: `${Math.round(elapsed * 100)}%` }}
              transition={{ duration: 0.6 }}
            />
          </div>
        )}
      </button>

      {occ.requiresCheck && (
        <div className="px-4 pb-4">
          <AnimatePresence mode="wait" initial={false}>
            {status ? (
              <motion.button
                key="status"
                type="button"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  onOpen(occ);
                }}
                className={`tap flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-semibold ${
                  status === 'done'
                    ? 'bg-done-soft text-done-ink'
                    : status === 'missed'
                      ? 'bg-missed-soft text-missed-ink'
                      : 'bg-surface-2 text-muted'
                }`}
              >
                {status === 'done' ? (
                  <Check size={18} strokeWidth={3} />
                ) : (
                  <X size={18} strokeWidth={3} />
                )}
                {status === 'done'
                  ? 'Opravljeno'
                  : status === 'missed'
                    ? 'Ni opravljeno'
                    : 'Preskočeno'}
                <span className="font-normal">· spremeni</span>
              </motion.button>
            ) : (
              <motion.div
                key="actions"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-[1fr_auto] gap-2"
              >
                <button
                  type="button"
                  onClick={() => {
                    onStatus(occ, 'done');
                  }}
                  className="tap flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-done text-[16px] font-semibold text-on-status"
                >
                  <Check size={20} strokeWidth={3} /> Opravljeno
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onStatus(occ, 'missed');
                  }}
                  aria-label="Ni opravljeno"
                  className="tap flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-surface-2 px-4 text-[15px] font-semibold text-muted"
                >
                  <X size={18} strokeWidth={3} /> Ni
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
